import * as React from 'react';

export type MappedArrayObject<T> = {
  [key: string]: T;
};

export type GroupedArrayObject<T> = {
  [key: string]: T[];
};

export function arrayToObject<T>(array: T[], key: (item: T) => string): MappedArrayObject<T> {
  const obj: MappedArrayObject<T> = {};

  for (let item of array) {
    obj[key(item)] = item;
  }

  return obj;
}

export function groupArray<T>(array: T[], key: (item: T) => string): GroupedArrayObject<T> {
  const obj: GroupedArrayObject<T> = {};

  for (let item of array) {
    const itemKey = key(item);
    (obj[itemKey] || (obj[itemKey] = [])).push(item);
  }

  return obj;
}

export function camelCase(value?: string) {
  if (!value || value.length <= 1) {
    return value ?? '';
  }

  return value.charAt(0).toLocaleLowerCase() + value.substring(1);
}

const formatMustasch = /{([^{}]*)}/g;

/**
 * Format a string template in a provided mustasch format using an object as a lookup source.
 *
 * @example
 * formatObject('test {key1} format', { key1: 'string' }) // "test string format"
 *
 * @param format Format string template.
 * @param object Object to use as lookup.
 * @param replacementFunc Optional callback function for each parsed tag.
 * Returned `string` will be used instead of given mapped item.
 * @returns Formatted string template.
 */
export function formatObject(format: string, object?: any, replacementFunc?: (key: string, item: any) => string) {
  if (object == null) {
    return '';
  }

  return format.replace(formatMustasch, (a: string, b: string) => {
    let val = object[b] ?? object[camelCase(b)];
    let formatVal = replacementFunc?.(b, val);
    return formatVal ?? val;
  });
}

const byte = 1024;
export const FileSizeUnits = {
  KiB: byte,
  MiB: byte * byte,
  GiB: byte * byte * byte,
  TiB: byte * byte * byte * byte,
  PiB: byte * byte * byte * byte * byte,
  EiB: byte * byte * byte * byte * byte * byte
};

/**
 * Returns a new array with one item replaced at given index.
 * Does not mutate the original array.
 * @param items The original array
 * @param index What item to replace
 * @param newItem The new replacementitem
 */
export function replaceItem<T>(items: T[], index: number, newItem: T) {
  return items.map((item, idx) => idx === index ? newItem : item);
}

export const FileExtensions = {
  PDF: '.pdf',
  MP4: '.mp4',
  OGG: '.ogg',
  SVG: '.svg',
  GIF: '.gif'
};

export function thousandSeparator(num: number) {
  return num.toString().replace(/\B(?=(\d{3})+(?!\d))/g, ' ');
};

// eslint-disable-next-line no-shadow
export enum Direction {
  prev,
  next
}

// eslint-disable-next-line no-shadow
export enum ThemeIds {
  raw = 'Raw'
}

export function parseStyleString(styleString: string) {
  const styleObj: React.CSSProperties = {};

  if (!styleString) {
    return styleObj;
  }

  styleString.split(';').forEach(style => {
    const [property, value] = style.split(':').map(str => str.trim());

    if (property && value) {
      (styleObj as any)[property] = value;
    }
  });

  return styleObj;
}

export function useDebounce<T>(value: T, delay = 500) {
  const [debouncedValue, setDebouncedValue] = React.useState<T>();
  const timerRef = React.useRef<number>(0);

  React.useEffect(() => {
    timerRef.current = setTimeout(() => setDebouncedValue(value), delay) as unknown as number;

    return () => {
      clearTimeout(timerRef.current);
    };
  }, [value, delay]);

  return debouncedValue;
}

export function load(type: string, options: {}, target: Node = document.body) {
  const element = document.createElement(type);
  Object.assign(element, options);
  target.appendChild(element);

  return {
    element,
    destroy() {
      element.remove()
    }
  };
}